<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    userComponentProps,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcRegister',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save', 'saved', 'deleted']);

  const props = defineProps({
    ...userComponentProps,
    oid: {
      type: String,
      required: false,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const getPayload = (...args) => {
    const [editor] = args;

    if (editor.isNew) {
      console.log(editor.getChanges({ skipEmbeddedLists: true }));
      return editor.getChanges({ skipEmbeddedLists: true });
    }

    return {
      id: editor.id,
      etag: editor.getETAG(),
      ...editor.getChanges({ skipEmbeddedLists: true }),
    };
  };

  const messages = ref({
    submitNewItem: $fn.value.pgettext('Register|Command', 'Create register'),
    saveItem: $fn.value.pgettext('Register|Command', 'Save changes'),
  });
  const formName = ref('register');
  const labelAlign = ref(null);
  const formId = ref(null);
  const item = ref(null);
  const editor = ref(null);
  const ux = ref([]);
  const b_6126985650642047_dataSource = computed(() => ({
    name: 'suggest_company',
    parameters: {
      role: 'own',
    },
  }));

  const $data = computed(() => ({
    messages: messages.value,
    formName: formName.value,
    labelAlign: labelAlign.value,
    formId: formId.value,
    item: item.value,
    editor: editor.value,
    ux: ux.value,
    b_6126985650642047_dataSource: b_6126985650642047_dataSource.value,
  }));

  const $$load_labelAlign = () => {
    return $config.value.forms.defaultLabelAlign;
  };
  const $$load_formId = () => {
    return $fn.value.randomElementId();
  };
  const $$load_item = () => {
    return props.oid
      ? $fn.value.fetch('read_register', {
          asResource: true,
          parameters: { id: props.oid },
        })
      : $fn.value.schemaDefaults('CreateRegister');
  };
  const $$load_editor = () => {
    return item.value.editor();
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'messages' },
      { name: 'formName' },
      { name: 'labelAlign' },
      { name: 'formId' },
      { name: 'item' },
      { name: 'editor', dependsOn: ['item'] },
      { name: 'ux' },
      { name: 'b_6126985650642047_dataSource', nodeId: '6126985650642047' },
    ],
    loaders: {
      labelAlign: $$load_labelAlign,
      formId: $$load_formId,
      item: $$load_item,
      editor: $$load_editor,
    },
    ux: {},
    dataRefs: {
      messages,
      formName,
      labelAlign,
      formId,
      item,
      editor,
      ux,
      b_6126985650642047_dataSource,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.oid,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('oid');
    }
  );

  const act_8564662037462133_emit_9tw4o27oi_getActionArgs = async ($event) => {
    return {
      event: 'saved',
      value: $event.data,
    };
  };
  const act_8564662037462133_crud_gkdcu79sa_getActionArgs = async ($event) => {
    return {
      objectType: 'register',
      op: 'upsert',
      data: getPayload(editor.value),
    };
  };
  const act_655921014111650_emit_yeekzz91p_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
      value: editor.value,
    };
  };
  const act_655921014111650_crud_6v9umfixe_getActionArgs = async ($event) => {
    return {
      objectType: 'register',
      op: 'delete',
      data: getPayload(editor.value),
    };
  };
  const act_655921014111650_crud_6v9umfixe_getConfirm = async ($event) => {
    return {
      header: $fn.value.pgettext(
        'Register|Header',
        'Please confirm deleting this register.'
      ),
      message: $fn.value.pgettext('Register', 'This action cannot be undone.'),
      btnOk: $fn.value.gettext('Delete'),
    };
  };
  const act_1238468668488283_emit_hxez6qntw_getActionArgs = async ($event) => {
    return {
      event: 'deleted',
      value: {},
    };
  };
  const inst_evh_1238468668488283_clickPreventStop_cly4va25v_getWhen = async (
    $event
  ) => {
    return item.value.isNew;
  };

  const act_7315092382398562_reloadComponentData_5dyuzwog2 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_7fvdue5mm =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_5dyuzwog2,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_7fvdue5mm],
    event: 'reload',
  });
  const act_8564662037462133_emit_9tw4o27oi = new actionTypes.EmitAction({
    actionArgs: act_8564662037462133_emit_9tw4o27oi_getActionArgs,
  });
  const inst_evh_8564662037462133_success_xiwdeqe31 =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_emit_9tw4o27oi,
      event: 'success',
      displayName: 'emit',
    });
  const act_8564662037462133_crud_gkdcu79sa = new actionTypes.CRUDAction({
    actionArgs: act_8564662037462133_crud_gkdcu79sa_getActionArgs,
    displayName: 'save',
    events: [inst_evh_8564662037462133_success_xiwdeqe31],
  });
  const inst_evh_8564662037462133_submit_uwua119fu =
    new actionTypes.EventHandlerImpl({
      action: act_8564662037462133_crud_gkdcu79sa,
      event: 'submit',
      displayName: 'save',
    });
  const inst_evh_8564662037462133_submit = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_8564662037462133_submit_uwua119fu],
    event: 'submit',
  });
  const act_655921014111650_emit_yeekzz91p = new actionTypes.EmitAction({
    actionArgs: act_655921014111650_emit_yeekzz91p_getActionArgs,
  });
  const inst_evh_655921014111650_success_7crnbxmre =
    new actionTypes.EventHandlerImpl({
      action: act_655921014111650_emit_yeekzz91p,
      event: 'success',
      displayName: 'emit',
    });
  const act_655921014111650_crud_6v9umfixe = new actionTypes.CRUDAction({
    actionArgs: act_655921014111650_crud_6v9umfixe_getActionArgs,
    confirm: act_655921014111650_crud_6v9umfixe_getConfirm,
    displayName: 'delete',
    events: [inst_evh_655921014111650_success_7crnbxmre],
  });
  const inst_evh_655921014111650_clickPreventStop_kwtq9y1uk =
    new actionTypes.EventHandlerImpl({
      action: act_655921014111650_crud_6v9umfixe,
      event: 'click.prevent.stop',
      displayName: 'delete',
    });
  const inst_evh_655921014111650_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_655921014111650_clickPreventStop_kwtq9y1uk],
      event: 'click.prevent.stop',
    });
  const act_1238468668488283_emit_hxez6qntw = new actionTypes.EmitAction({
    actionArgs: act_1238468668488283_emit_hxez6qntw_getActionArgs,
  });
  const inst_evh_1238468668488283_clickPreventStop_cly4va25v =
    new actionTypes.EventHandlerImpl({
      action: act_1238468668488283_emit_hxez6qntw,
      event: 'click.prevent.stop',
      when: inst_evh_1238468668488283_clickPreventStop_cly4va25v_getWhen,
      displayName: 'emit',
    });
  const inst_evh_1238468668488283_clickPreventStop =
    new actionTypes.EventHandlerGroup({
      handlers: [inst_evh_1238468668488283_clickPreventStop_cly4va25v],
      event: 'click.prevent.stop',
    });
  const act_1889980785424776_reloadComponentData_0888jeca1 =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_1889980785424776_reload_y81uow33c =
    new actionTypes.EventHandlerImpl({
      action: act_1889980785424776_reloadComponentData_0888jeca1,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_1889980785424776_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_1889980785424776_reload_y81uow33c],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_7fvdue5mm,
    inst_evh_7315092382398562_reload,
    inst_evh_8564662037462133_success_xiwdeqe31,
    inst_evh_8564662037462133_submit_uwua119fu,
    save: inst_evh_8564662037462133_submit_uwua119fu,
    inst_evh_8564662037462133_submit,
    inst_evh_655921014111650_success_7crnbxmre,
    inst_evh_655921014111650_clickPreventStop_kwtq9y1uk,
    delete: inst_evh_655921014111650_clickPreventStop_kwtq9y1uk,
    inst_evh_655921014111650_clickPreventStop,
    inst_evh_1238468668488283_clickPreventStop_cly4va25v,
    inst_evh_1238468668488283_clickPreventStop,
    inst_evh_1889980785424776_reload_y81uow33c,
    inst_evh_1889980785424776_reload,
  };

  const evh_7315092382398562_reload_7fvdue5mm = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_7fvdue5mm.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_success_xiwdeqe31 = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_success_xiwdeqe31.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_submit_uwua119fu = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_submit_uwua119fu.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_8564662037462133_submit = async (event, scope) => {
    return await actions.inst_evh_8564662037462133_submit.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_655921014111650_success_7crnbxmre = async (event, scope) => {
    return await actions.inst_evh_655921014111650_success_7crnbxmre.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_655921014111650_clickPreventStop_kwtq9y1uk = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_655921014111650_clickPreventStop_kwtq9y1uk.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_655921014111650_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_655921014111650_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1238468668488283_clickPreventStop_cly4va25v = async (
    event,
    scope
  ) => {
    return await actions.inst_evh_1238468668488283_clickPreventStop_cly4va25v.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1238468668488283_clickPreventStop = async (event, scope) => {
    return await actions.inst_evh_1238468668488283_clickPreventStop.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_reload_y81uow33c = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_reload_y81uow33c.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_1889980785424776_reload = async (event, scope) => {
    return await actions.inst_evh_1889980785424776_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div v-if="loaded">
      <ItpForm
        :id="formId"
        :name="formName"
        :readonly="item.locked"
        :labelAlign="$config.forms.defaultLabelAlign"
        autofocus="first"
        @submit="evh_8564662037462133_submit($event, {})"
      >
        <ItpRow>
          <ItpCol :w="12">
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="5"
              name="name"
              :label="pgettext('Register', 'Name')"
              required
            >
              <ItpFormInput
                name="name"
                v-model="editor.name"
                required
                :placeholder="pgettext('Register', 'The name of the register.')"
                autofocus
              >
              </ItpFormInput>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="5"
              name="company"
              :label="pgettext('Register', 'Company')"
              required
            >
              <ItpFormSearchableSelect
                name="company"
                v-model="editor.company"
                :dataSource="b_6126985650642047_dataSource"
                prefetch
                bind-object
                textField="name"
                required
              >
              </ItpFormSearchableSelect>
            </ItpFormGroup>
            <ItpFormGroup
              :label-cols-md="3"
              :content-cols-md="5"
              name="notes"
              :label="pgettext('Register', 'Notes')"
              :labelAlign="labelAlign"
            >
              <ItpFormTextarea name="notes" v-model="editor.notes">
              </ItpFormTextarea>
            </ItpFormGroup>
            <ItpFormGroup :label-cols-md="3" :content-cols-md="9">
              <ItpButton
                type="submit"
                :text="item.isNew ? messages.submitNewItem : messages.saveItem"
                :form="formId"
                :spinning="actions.save.isRunning"
              >
              </ItpButton>
              <ItpButton
                :text="pgettext('Register', 'Delete')"
                variant="danger"
                v-if="!item.isNew"
                @click.prevent.stop="
                  evh_655921014111650_clickPreventStop($event, {})
                "
              >
              </ItpButton>
              <ItpButton
                :text="pgettext('Register', 'Cancel')"
                variant="default"
                @click.prevent.stop="
                  evh_1238468668488283_clickPreventStop($event, {})
                "
              >
              </ItpButton>
            </ItpFormGroup>
          </ItpCol>
        </ItpRow>
      </ItpForm>
      <hr />
      <UcRegisterVersions
        :register="item"
        :reload="lastLoaded"
        v-if="!item.isNew"
        @reload="evh_1889980785424776_reload($event, {})"
      >
      </UcRegisterVersions>
    </div>
  </ItpUsercontrol>
</template>
